import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  ColorPicker,
  Divider,
  Form,
  Input,
  Radio,
  Tabs,
  Tag,
} from 'antd';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { UnsavedChangesScopeEnum } from '../../../enums/UnsavedChangesScopeEnum';
import { useFormDirtyState } from '../../../hooks/useFormDirtyState';
import { useRequestLeave } from '../../../hooks/useRequestLeave';
import { createGuardedModalCancelHandler } from '../../../utils/openGuardedFormModal';
import { Icon } from '../../Core/Icon';
import { useTagForm } from '../../../hooks/useTagForm';
import { useColorScheme } from '../../../hooks/useColorScheme/useColorScheme';
import type TagInterface from '../../../interfaces/Tag';
import tagColors from '../../../lib/tagColors';
import { FileService } from '../../../services/fileApiService';
import { getTagPresentationStyle } from '../../../utils/colorsUtilis';
import TagAccessTab from '../../Common/AccessMap/TagAccessTab';

const fileService = new FileService();

export type TagFormTabKey = 'edit' | 'access';

interface Props {
  tag: TagInterface | null;
  onSubmit: (newTag: TagInterface) => void;
  onCancel: () => void;
  /** Active tab when Access & Protections is available (edit mode only). */
  initialTab?: TagFormTabKey;
}

export const TagForm = ({ tag, onSubmit, onCancel, initialTab = 'edit' }: Props) => {
  const [resolvedTag, setResolvedTag] = useState<TagInterface | null>(null);
  const [isLoadingTag, setIsLoadingTag] = useState(false);
  const { resolvedColorScheme } = useColorScheme();
  const isDarkMode = resolvedColorScheme === 'dark';

  // In edit mode, fetch full tag (userIds, groupIds) from API; keep passing partial tag until loaded
  useEffect(() => {
    if (tag) {
      setIsLoadingTag(true);
      setResolvedTag(null);
      fileService
        .getTag(tag.id)
        .then((fullTag) => setResolvedTag(fullTag as TagInterface))
        .finally(() => setIsLoadingTag(false));
    } else {
      setResolvedTag(null);
      setIsLoadingTag(false);
    }
  }, [tag?.id]);

  const tagToUse = tag ? (resolvedTag ?? tag) : null;

  const {
    form,
    containerRef,
    handleFinish,
    handleColorSelect,
    handleOpenUserGroupSelection,
    selectedCount,
    isPrivateTag,
    t,
  } = useTagForm({ tag: tagToUse, onSubmit });

  const requestLeave = useRequestLeave();
  const { markDirty, resetDirty } = useFormDirtyState({
    scope: UnsavedChangesScopeEnum.Modal,
    onDiscard: () => form.resetFields(),
  });

  const handleCancel = createGuardedModalCancelHandler({
    onConfirmed: onCancel,
    requestLeave,
    resetForm: () => form.resetFields(),
  });

  const handleFormFinish = (values: Parameters<typeof handleFinish>[0]) => {
    resetDirty();
    handleFinish(values);
  };

  // Watch fields so preview re-renders (getFieldValue alone does not subscribe).
  const selectedColor = Form.useWatch('color', form);
  const previewTitle = Form.useWatch('title', form);

  const editForm = (
    <Form
      layout="vertical"
      form={form}
      size="large"
      className="pt-6! [&_.ant-form-item]:mb-2! [&_.ant-form-item-explain-error]:hidden! [&_.ant-form-item-label]:hidden!"
      onFinish={handleFormFinish}
      onValuesChange={markDirty}
    >
      <Form.Item name="title" label={t('common.title')} rules={[{ required: true }]}>
        <Input placeholder={t('common.title')} autoFocus maxLength={35} disabled={isLoadingTag} />
      </Form.Item>

      <Form.Item name="color" label={t('common.color')} rules={[{ required: true }]}>
        <div className="flex flex-row gap-2">
          {tagColors.map((color) => (
            <Button
              key={color.text}
              type="text"
              size="small"
              disabled={isLoadingTag}
              onClick={() => handleColorSelect(color.text)}
            >
              <Avatar
                className={clsx(
                  'border-gray! bg-white!',
                  selectedColor === color.text && 'border-2!',
                )}
                style={{
                  borderColor: selectedColor === color.text ? color.text : 'lightgray',
                  color: color.text,
                }}
                size="small"
                shape="square"
              >
                A
              </Avatar>
            </Button>
          ))}
          <ColorPicker
            size="small"
            className="w-full! py-1!"
            value={selectedColor}
            disabled={isLoadingTag}
            showText={() => <span>{selectedColor || t('common.selectColor')}</span>}
            onChangeComplete={(e) => {
              const hexColor = e.toHexString();
              handleColorSelect(hexColor);
            }}
          />
        </div>
      </Form.Item>

      <Divider className="my-6!" titlePlacement="start">
        {t('common.preview')}
      </Divider>

      <Tag
        aria-label={previewTitle || t('common.tagTitle')}
        style={
          selectedColor
            ? getTagPresentationStyle(selectedColor, { darkMode: isDarkMode })
            : undefined
        }
        className="h-10! w-full!"
      >
        <div className="flex h-full flex-row items-center justify-center gap-2">
          <span className="text-lg">{previewTitle || t('common.tagTitle')}</span>
        </div>
      </Tag>

      <Divider className="mt-6!" titlePlacement="start">
        {t('modal.tagForm.visibility')}
      </Divider>
      <p className="text-gray-dark pb-4 text-sm">{t('modal.tagForm.visibilityDescription')}</p>
      <div className="flex flex-col gap-2">
        <Form.Item
          name="publicTag"
          label={t('common.companyTag')}
          className="mb-4"
          initialValue={false}
          rules={[{ required: true, message: t('common.select') }]}
        >
          <Radio.Group className="flex! flex-row! gap-2!" disabled={isLoadingTag}>
            <Radio value={true}>{t('modal.groupForm.allUsers')}</Radio>
            <Radio value={false}>{t('modal.groupForm.selectedUsersOrGroups')}</Radio>
          </Radio.Group>
        </Form.Item>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <p className="text-gray-dark text-sm">{t('modal.tagForm.selectUserRoles')}:</p>
            <div className="flex flex-row gap-2">
              <Form.Item name="contributor" valuePropName="checked">
                <Checkbox disabled={isLoadingTag}>{t('roles.2001')}</Checkbox>
              </Form.Item>
              <Form.Item name="reader" valuePropName="checked">
                <Checkbox disabled={isLoadingTag}>{t('roles.3002')}</Checkbox>
              </Form.Item>
              <Form.Item name="external" valuePropName="checked">
                <Checkbox disabled={isLoadingTag}>{t('roles.4001')}</Checkbox>
              </Form.Item>
            </div>
          </div>
          <div className="flex flex-row items-center justify-end">
            <Badge size="small" count={selectedCount}>
              <Button
                size="middle"
                icon={<Icon name="UserIcon" className="h-4 w-4" />}
                onClick={handleOpenUserGroupSelection}
                disabled={!isPrivateTag || isLoadingTag}
              >
                {t('common.manageUsersAndGroups')}
              </Button>
            </Badge>
          </div>
        </div>
      </div>

      <Divider />
      <div className="flex flex-row items-center justify-end gap-2">
        <Button size="middle" onClick={handleCancel} disabled={isLoadingTag}>
          {t('common.cancel')}
        </Button>
        <Button size="middle" type="primary" htmlType="submit" loading={isLoadingTag}>
          {t('common.save')}
        </Button>
      </div>
    </Form>
  );

  if (!tag?.id || !tagToUse) {
    return (
      <div ref={containerRef} style={{ position: 'relative' }}>
        {editForm}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <Tabs
        defaultActiveKey={initialTab}
        items={[
          { children: editForm, key: 'edit', label: t('common.edit') },
          {
            children: <TagAccessTab tag={tagToUse} />,
            key: 'access',
            label: t('accessMap.tagAccessTabLabel'),
          },
        ]}
      />
    </div>
  );
};

export default TagForm;
